import React, { useEffect, useMemo, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Card from "../../../Card";
import ValidCredentialsAlert from "../../alerts/ValidCredentialAlert";
import DeleteButtonAccount from "../../buttons/DeleteButtonAccount.jsx";
import CredentialField from "../CredentialField";
import AccountSection from "../AccountSection";
import DangerZone from "../DangerZone";
import { validateField } from "../validators";
import {
  PROVIDER_SCHEMAS,
  allFields,
  buildSubmitPayload,
  buildTestPayload,
  requiredFields,
} from "../providerSchemas";
import { dispatchSaveCredentials, dispatchTestConnection } from "../accountThunkMap";
import { updateNameNewAccount, resetStateCredentials } from "../../../../store/slices/accounts/accountSlice";
import { getCredentialsFromAccount } from "../../../../store/slices/accounts/accountThunks";
import { setCurrentIdAccount } from "../../../../store/slices/contracts/contractsSlices";
import "../../../../styles/accountForm.css";

// Los secretos llegan como objeto cuando vienen de GCP (JSON parseado) y
// como string en el resto. El textarea siempre necesita un string.
const asText = (raw) => {
  if (raw === null || raw === undefined) return "";
  return typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
};

const StatusChip = ({ kind, children }) => (
  <span className={`cco-chip cco-chip-${kind}`}>{children}</span>
);

const AccountEditForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useAuth0();

  const { idTenant, cardUsageAccount } = useSelector((state) => state.costing);
  const { validCredentials, stateAccount, nameNewAccount, dataAccountToEdit, indexOfAccountToEdit } =
    useSelector((state) => state.accounts);

  const account = cardUsageAccount[indexOfAccountToEdit];
  const provider = account.cloudProvider;
  const schema = PROVIDER_SCHEMAS[provider];

  const [values, setValues] = useState({});
  const [stored, setStored] = useState({});
  const [touched, setTouched] = useState({});
  const [replaceCredentials, setReplaceCredentials] = useState(false);
  const [saving, setSaving] = useState(false);

  const fields = useMemo(() => allFields(schema), [schema]);
  const required = useMemo(() => requiredFields(schema, { includePostTest: true }), [schema]);

  useEffect(() => {
    dispatch(getCredentialsFromAccount({ accountId: account.accountId }));
  }, []);

  // `dataAccountToEdit` llega async desde el API, así que los valores se
  // siembran cuando baja, no en el primer render.
  useEffect(() => {
    if (!dataAccountToEdit || Object.keys(dataAccountToEdit).length === 0) return;
    const next = {};
    const keep = {};
    fields.forEach((field) => {
      keep[field.key] = asText(dataAccountToEdit[field.key]);
      // Un secret nunca se precarga editable: si queda vacío se reusa el
      // valor guardado, así no hay forma de pisarlo por accidente.
      next[field.key] = field.secret ? "" : keep[field.key];
    });
    setValues(next);
    setStored(keep);
  }, [dataAccountToEdit]);

  const handleChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setTouched((current) => ({ ...current, [key]: true }));
  };

  // Vacío en un secret significa "mantener lo guardado".
  const merged = () => {
    const result = { ...values };
    fields.forEach((field) => {
      if (field.secret && !String(result[field.key] || "").trim() && stored[field.key]) {
        result[field.key] = stored[field.key];
      }
    });
    return result;
  };

  const isEmpty = (field) => String(merged()[field.key] || "").trim() === "";
  const missing = required.filter(isEmpty);
  const testSatisfied = !replaceCredentials || validCredentials === true;
  const canSave = missing.length === 0 && testSatisfied && !saving;

  const handleTest = () => {
    dispatch(
      dispatchTestConnection(dispatch, provider, buildTestPayload(schema, merged()), user.email)
    );
  };

  const handleReplaceCredentials = () => {
    // `resetStateCredentials` no toca dataAccountToEdit ni nameNewAccount,
    // solo limpia el resultado del test anterior.
    dispatch(resetStateCredentials());
    setReplaceCredentials(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await dispatchSaveCredentials(dispatch, provider, {
      data: buildSubmitPayload(schema, nameNewAccount, merged()),
      idTenant,
      user: user.email,
      accountId: account.accountId,
    });
    // Antes esto era un <Link to='/accounts'> que navegaba antes de que
    // el thunk resolviera, así que un submit lento se perdía en silencio.
    if (saved) history.push("/accounts");
    else setSaving(false);
  };

  const handleCancel = () => {
    dispatch(resetStateCredentials());
    history.push("/accounts");
  };

  const handleGoToContracts = () => {
    dispatch(
      setCurrentIdAccount({
        idAccount: account.accountId,
        region: dataAccountToEdit.account,
        creationUser: user.email,
      })
    );
  };

  const sectionStatus = (sectionFields) => {
    const pending = sectionFields.filter((field) => field.required && isEmpty(field)).length;
    if (pending > 0) return <StatusChip kind="warn">{pending} to complete</StatusChip>;
    return <StatusChip kind="ok">&#10003; Complete</StatusChip>;
  };

  return (
    <Card className="cco-account-form">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="header-title">
          <h4 className="card-title">Edit Account</h4>
        </div>
        <StatusChip kind="neutral">{schema.label}</StatusChip>
      </Card.Header>

      <Card.Body>
        <ValidCredentialsAlert />

        <section className="cco-section">
          <AccountSection
            index={1}
            title="Identity"
            status={
              nameNewAccount.trim() ? (
                <StatusChip kind="ok">&#10003; Complete</StatusChip>
              ) : (
                <StatusChip kind="warn">1 to complete</StatusChip>
              )
            }
          />
          <div className="d-flex" style={{ gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Group className="form-group" controlId="cco-accountName">
                <Form.Label className="cco-label">
                  Account Name<span className="cco-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={nameNewAccount}
                  maxLength={25}
                  onChange={(event) => dispatch(updateNameNewAccount(event.target.value))}
                  placeholder="Enter Account Name"
                  style={{ borderColor: "#899AF1", color: "#000" }}
                />
                <div className="cco-hint">
                  Label shown across the dashboard. Max 25 characters.
                </div>
              </Form.Group>
            </div>
            <div style={{ width: 210 }}>
              <Form.Label className="cco-label">Cloud Provider</Form.Label>
              <StatusChip kind="neutral">
                {schema.label} &#128274;
              </StatusChip>
              <div className="cco-hint">Cannot be changed on an existing account.</div>
            </div>
          </div>
        </section>

        {schema.sections.map((section, position) => {
          const hasSecret = section.fields.some((field) => field.secret);
          const collapsed = hasSecret && !replaceCredentials;

          return (
            <section className="cco-section" key={section.id}>
              <AccountSection
                index={position + 2}
                title={section.title}
                status={
                  collapsed ? (
                    <StatusChip kind="neutral">Unchanged</StatusChip>
                  ) : (
                    sectionStatus(section.fields)
                  )
                }
              />

              {collapsed ? (
                <div>
                  <details className="cco-disc">
                    <summary>
                      <span style={{ color: "#3f51b5" }}>&#128273;</span>
                      View stored credentials
                      <span className="cco-hint" style={{ marginTop: 0 }}>
                        &mdash; shown in clear text, not masked
                      </span>
                      <span className="cco-caret">&#9662;</span>
                    </summary>
                    <div className="cco-disc-body">
                      {section.fields.map((field) => (
                        <Form.Group className="form-group" key={`stored-${field.key}`}>
                          <Form.Label className="cco-label">{field.label}</Form.Label>
                          <Form.Control
                            as={field.multiline ? "textarea" : "input"}
                            rows={field.multiline ? 6 : undefined}
                            disabled
                            value={stored[field.key] || ""}
                            style={{
                              borderColor: "#899AF1",
                              color: "#5b5a72",
                              background: "#f4f5fa",
                              fontFamily: "ui-monospace, Menlo, monospace",
                              fontSize: 12.5,
                            }}
                          />
                        </Form.Group>
                      ))}
                      <div className="cco-hint">
                        The stored value is kept server-side. Leave the field empty to keep it.
                      </div>
                    </div>
                  </details>
                  <Button
                    type="button"
                    variant="btn btn-outline-primary"
                    size="sm"
                    className="mt-3"
                    onClick={handleReplaceCredentials}
                  >
                    &#128273; Replace credentials
                  </Button>
                  <div className="cco-hint">You will need to re-run the connection test.</div>
                </div>
              ) : (
                <div
                  className="cco-grid"
                  style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
                >
                  {section.fields.map((field) => (
                    <CredentialField
                      key={field.key}
                      field={field}
                      value={values[field.key] || ""}
                      error={
                        touched[field.key] ? validateField(field, values[field.key]) : null
                      }
                      onChange={handleChange}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {replaceCredentials && validCredentials !== true ? (
          <div className="mb-2">
            <Button type="button" onClick={handleTest} variant="outline-success">
              {stateAccount === "ready" ? schema.testLabel : "Loading..."}
              {stateAccount !== "ready" && (
                <Spinner animation="grow" size="sm" className="ms-2" />
              )}
            </Button>
            <div className="cco-hint">
              Run the connection test before you can save the new credentials.
            </div>
          </div>
        ) : null}
      </Card.Body>

      <div className="cco-actionbar">
        <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
          <span className="cco-counter">
            {required.length - missing.length} of {required.length} required fields complete
          </span>
          {missing.length === 0 && testSatisfied ? (
            <StatusChip kind="ok">&#10003; Ready to save</StatusChip>
          ) : null}
        </div>
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          {provider === "Snowflake" && (
            <Link to="/contracts">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleGoToContracts}
              >
                Manage contracts
              </Button>
            </Link>
          )}
          <Button type="button" variant="link" className="cco-btn-ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            className="cco-btn-primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </div>

      <DangerZone
        confirmHint={
          <>
            You will be asked to type <b>{account.accountName}</b> to confirm.
          </>
        }
      >
        <DeleteButtonAccount index={indexOfAccountToEdit} label="Delete account" />
      </DangerZone>
    </Card>
  );
};

export default AccountEditForm;
